import React from 'react';
import {
  createBrowserRouter,
  Link,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import PageLayout from './components/page-layout/page-layout';
import Page from './components/page/page';
import { novaRoutes } from '@/nova/router';
import { configuratorV0Route } from '@/nova/pages/configurator-v0/router';
import { customizerRoute } from '@/nova/pages/customizer/router';
import { loginRoute } from '@/nova/pages/login/router';
import { scrollKeyOf } from '@/nova/pages/creatives/asset-routes';
import { telegramCallbackRoute } from '@/nova/pages/social-login/telegram-callback/router';
import { verifyEmailRoute } from '@/nova/pages/social-login/verify-email/router';
import { registerRoute } from '@/nova/pages/register/router';
import { resetPasswordRoute, resetPasswordEmailRoute } from '@/nova/pages/reset-password/router';
import { changePasswordRoute } from '@/nova/pages/change-password/router';
import RootRedirect from './components/fixed-routes/root-redirect';
import { DEFAULT_LAYOUT, LAYOUTS } from '@/nova/layouts';
import { useAppSelector } from './store';
import PageLoader from './components/page-loader';
import ActionUIHandler from './components/action-handler';
import Popups from './components/popups/popups';
import RouteChangeHandler from './components/route-change-handler';
import StudioBreadcrumbNav from './components/studio-breadcrumb-nav';
import AnalyticsConsent from './components/analytics-consent';
import PulsarIdentity from './components/pulsar-identity';
import { useTranslation } from 'react-i18next';
import { START_OR_KEY, START_CURRENCY_KEY } from '@/utils/start-properties-market';
import { OWNERS_HUB_ROUTES } from '@/constants/routes';

type BaseElementProps = {
  children?: any;
};

const BaseElement = (props: BaseElementProps) => {
  const isLiveAgentEnabled = window.config.customerSupport?.liveChatEnabled === 'liveAgent';
  const isZendeskChatEnabled = window.config.customerSupport?.liveChatEnabled === 'zendesk';
  const isZohoChatEnabled = window.config.customerSupport?.liveChatEnabled === 'zoho';

  const { i18n } = useTranslation();
  const navigate = useNavigate();

  // Make internal, same-tab anchor clicks navigate client-side (SPA) instead of
  // letting the browser do a full page reload. Config-driven menus (left rail,
  // etc.) render plain `<a href="/...">` items via various components; a full
  // reload tears down the app and kills in-app view transitions (e.g. the
  // nova slide between Settings / Pulse and the rest). This single
  // delegated handler catches them all, regardless of which component rendered
  // the link. It deliberately skips: modified / non-primary clicks (so
  // cmd/ctrl/middle-click still open a new tab), `target` other than `_self`,
  // `download` links, external / protocol-relative URLs, and anything a handler
  // already dealt with (`defaultPrevented`, e.g. the patched Link/LinkContainer).
  React.useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const node = event.target as Element | null;
      const anchor = node && typeof node.closest === 'function' ? node.closest('a') : null;
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      // Internal absolute paths only ('/foo'); skip '//host', 'http(s):', '#', 'mailto:', etc.
      if (!href || href[0] !== '/' || href[1] === '/') return;
      const linkTarget = anchor.getAttribute('target');
      if (linkTarget && linkTarget !== '_self') return;
      if (anchor.hasAttribute('download')) return;
      event.preventDefault();
      navigate(href);
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [navigate]);

  React.useEffect(() => {
    const toggleTranslationKeys = (event: KeyboardEvent) => {
      // Ctrl/CMD + Shift + L
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key?.toLowerCase() === 'l') {
        const oldLanguage = i18n.language;
        i18n.changeLanguage('cimode');
        setTimeout(() => {
          i18n.changeLanguage(oldLanguage);
        }, 5000);
      }
    };
    // prevent multiple event listeners
    window.removeEventListener('keydown', toggleTranslationKeys);
    window.addEventListener('keydown', toggleTranslationKeys);
    return () => {
      window.removeEventListener('keydown', toggleTranslationKeys);
    };
  }, []);

  return (
    <React.Fragment>
      <Outlet />
      <ActionUIHandler />
      {/* An ad library and its editor share one scroll entry (scrollKeyOf), so opening an ad keeps the grid's place. */}
      <ScrollRestoration getKey={scrollKeyOf} />
      <Popups />
      <RouteChangeHandler />
      <StudioBreadcrumbNav />
      <AnalyticsConsent />
      <PulsarIdentity />
    </React.Fragment>
  );
};

const ErrorElement = (props: any) => {
  console.error('ErrorElement', props);
  const location = useLocation();
  console.log(location);
  return (
    <div className="d-flex flex-column align-items-center mt-4 justify-content-start w-100 h-100">
      <div>500. That&apos;s an error.</div>
      <div>The requested URL {location.pathname} was not found on this server or something is wrong with it.</div>
      <Link to="/">Go Home</Link>
    </div>
  );
};

export let storedRouter: any = null;

const Redirect = ({ url }: { url: string }) => {
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate(url, {
      replace: true,
    });
  }, []);
  return null;
};
// @ts-ignore
const RedirectMemo = React.memo(Redirect);

/**
 * Backward-compat redirects for the pre-payment configurator wizards, whose
 * address-bar routes were renamed (`/configurator-v3` → `/setup`,
 * `/configurator-v0` → `/easy-setup`). Old bookmarks — and, importantly, the
 * external marketing site's deep links (`/configurator-v3?startProperties=…`) —
 * keep working: the route matches `${from}/*`, so the base and every step path
 * are caught, and the step suffix + query string (the `startProperties` hand-off)
 * + hash are all carried through to the new path untouched. (`/setup` itself now
 * forwards on to `/easy-setup`, see {@link ParkedSetupRedirect}.)
 */
const LEGACY_CONFIGURATOR_REDIRECTS: ReadonlyArray<{ from: string; to: string }> = [
  { from: '/configurator-v3', to: '/setup' },
  { from: '/configurator-v0', to: '/easy-setup' },
];

const LegacyPathRedirect = ({ from, to }: { from: string; to: string }): React.ReactElement => {
  const location = useLocation();
  // pathname always starts with `from` (the route matched `${from}/*`); swap just
  // the prefix and preserve the remainder + query + hash.
  const suffix = location.pathname.slice(from.length);
  return <Navigate to={`${to}${suffix}${location.search}${location.hash}`} replace />;
};

/**
 * The Mexican-market wizards (`/easy-setup-mx`, `/setup-mx`) no longer have their own routes:
 * both variants were consolidated back into the base flows. An old MX deep link / bookmark
 * must still land the owner in the base flow BUT minted in MXN (SPEI checkout), so before
 * redirecting we write the durable market hand-off keys the session mint reads
 * (`START_OR_KEY = 'MEX'`, `START_CURRENCY_KEY = 'MXN'`). Those sessionStorage keys are the
 * actual mechanism; the `?or=MEX&currency=MXN` we keep on the target URL is for legibility
 * only (the resolver never reads them there).
 *
 * The writes run inline in render (not in an effect): `<Navigate>` commits during this
 * render, so an effect would fire too late to guarantee the keys are set before the base
 * flow mounts. The writes are idempotent, so a StrictMode double-render is harmless.
 * The step suffix + hash are preserved exactly like `LegacyPathRedirect`; existing query
 * params (e.g. a `startProperties` hand-off) are merged, not clobbered.
 */
const MxHandoffRedirect = ({ from, to }: { from: string; to: string }): React.ReactElement => {
  const location = useLocation();
  try {
    sessionStorage.setItem(START_OR_KEY, 'MEX');
    sessionStorage.setItem(START_CURRENCY_KEY, 'MXN');
  } catch {
    /* sessionStorage unavailable — best-effort; the URL params below still carry intent. */
  }
  const suffix = location.pathname.slice(from.length);
  const params = new URLSearchParams(location.search);
  params.set('or', 'MEX');
  params.set('currency', 'MXN');
  return <Navigate to={`${to}${suffix}?${params.toString()}${location.hash}`} replace />;
};

/**
 * The full `v3` wizard is PARKED: its code stays in the repo (`src/nova/pages/configurator-v3`), but
 * every owner is funnelled through the short `v0` flow at `/easy-setup`, so nothing renders v3.
 * `/setup` (old marketing / email links, bookmarks, and the legacy `/configurator-v3` and
 * `/setup-mx` redirects above) forwards there, keeping the query string (the `startProperties`
 * hand-off, Stripe's 3DS return params) and the hash. Only the checkout step has a v0 twin:
 * `/setup/finish` lands on `/easy-setup/payment` (so a 3DS return still reaches the step that
 * confirms it); every other v3 step lands on the flow's start.
 * To bring v3 back, mount `configuratorRoute` (`@/nova/pages/configurator-v3/router`) in place of
 * this redirect and drop `v3` from `PARKED_FLOW_REMAP` in `src/utils/configurator-flow.ts`.
 */
const ParkedSetupRedirect = (): React.ReactElement => {
  const location = useLocation();
  const step = location.pathname.slice(OWNERS_HUB_ROUTES.CONFIGURATOR_V3.length).split('/').filter(Boolean)[0];
  const target =
    step === 'finish' ? `${OWNERS_HUB_ROUTES.CONFIGURATOR_V0}/payment` : OWNERS_HUB_ROUTES.CONFIGURATOR_V0;
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
};

const Routes: React.FC = () => {
  const config = useAppSelector((state) => state.templatesConfig);

  if (!config.loaded) {
    return <PageLoader />;
  }

  const routes: any[] = [];
  const page_layouts: { [id: string]: boolean } = {};
  Object.keys(config.setPages).forEach((url) => {
    if (url === '*') return;
    const page = config.setPages[url];

    if (page.page_layout_id) {
      page_layouts[page.page_layout_id] = true;

      routes.push({
        path: url,
        element: <PageLayout id={page.page_layout_id} pageId={page.page_id} />,
        errorElement: <ErrorElement />,
      });

      return;
    }

    routes.push({
      path: url,
      element: <Page id={page.page_id} />,
      errorElement: <ErrorElement />,
    });
  });

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('wsid')) {
    Object.keys(page_layouts).forEach((id) => {
      routes.push({
        path: `/debug-page-layout/${id}`,
        element: <PageLayout id={id as string} />,
        errorElement: <ErrorElement />,
      });
    });
  }

  if (config.reroute) {
    try {
      const extraRouting: any = JSON.parse(config.reroute);

      Object.keys(extraRouting).forEach((k: any) => {
        const destUrl = typeof extraRouting[k] === 'string' ? extraRouting[k] : extraRouting[k]?.dest;

        if (destUrl && destUrl != '*' && extraRouting[k]?.route !== false) {
          if (config.setPages[destUrl] && !extraRouting[k]?.forceRedirect) {
            const page = config.setPages[destUrl];

            if (page.page_layout_id) {
              routes.push({
                path: k,
                element: <PageLayout id={page.page_layout_id} pageId={page.page_id} />,
                errorElement: <ErrorElement />,
              });

              return;
            }

            routes.push({
              path: k,
              element: <Page id={page.page_id} />,
              errorElement: <ErrorElement />,
            });
          } else {
            routes.push({
              path: k,
              element: <RedirectMemo url={destUrl} />,
              errorElement: <ErrorElement />,
            });
          }
        }
      });
    } catch (e) {
      console.error(e);
    }
  }

  if (typeof config.setPages['*'] !== 'undefined') {
    // we have a global page handler
    const page = config.setPages['*'];

    if (page.page_layout_id) {
      routes.push({
        path: '*',
        element: <PageLayout id={page.page_layout_id} pageId={page.page_id} />,
        errorElement: <ErrorElement />,
      });
    } else {
      routes.push({
        path: '*',
        element: <Page id={page.page_id} />,
        errorElement: <ErrorElement />,
      });
    }
  }

  // Fixed React routes for the nova area (no moduleKey/ModuleElement).
  // Each renders inside its hand-written layout (default `main` — side menu, top
  // bar, content card, Lumio chat, mobile nav; the layout owns the /login auth
  // gate). Prepended so they win React Router's specificity tie against
  // identical config paths.
  const fixedRoutes = novaRoutes.map((r) => {
    const Layout = LAYOUTS[r.layout ?? DEFAULT_LAYOUT];
    return {
      path: r.path,
      element: <Layout>{r.element}</Layout>,
      errorElement: <ErrorElement />,
    };
  });

  const router = createBrowserRouter([
    {
      element: <BaseElement />,
      // The root guard (`/`) is prepended first so a bare app load is resolved by
      // brand state (→ register / deploy wizard / home / setup wizard) and wins
      // the tie against the config-driven `/` page. Then the configurator routes
      // (the parked v3 `/setup` redirect and the full-bleed v0 wizard); then the layout-wrapped fixed routes
      // BEFORE the config routes — React Router breaks ties by list order, and
      // remote config registers IDENTICAL paths (/account/marketing,
      // /account/operations…) whose nova moduleKeys were removed, so
      // config must NOT win (it would render empty chrome).
      children: [
        { path: '/', element: <RootRedirect />, errorElement: <ErrorElement /> },
        { ...loginRoute, errorElement: <ErrorElement /> },
        { ...telegramCallbackRoute, errorElement: <ErrorElement /> },
        { ...verifyEmailRoute, errorElement: <ErrorElement /> },
        { ...registerRoute, errorElement: <ErrorElement /> },
        { ...resetPasswordRoute, errorElement: <ErrorElement /> },
        { ...resetPasswordEmailRoute, errorElement: <ErrorElement /> },
        { ...changePasswordRoute, errorElement: <ErrorElement /> },
        // The parked v3 wizard: `/setup/*` only forwards to `/easy-setup` (see ParkedSetupRedirect).
        {
          path: `${OWNERS_HUB_ROUTES.CONFIGURATOR_V3}/*`,
          element: <ParkedSetupRedirect />,
          errorElement: <ErrorElement />,
        },
        { ...configuratorV0Route, errorElement: <ErrorElement /> },
        { ...customizerRoute, errorElement: <ErrorElement /> },
        // The retired `-mx` wizards: mint MXN off the durable hand-off keys, then redirect
        // to the base flow (preserving the step suffix + query + hash). Placed ahead of the
        // config routes so a stale MX deep link resolves cleanly instead of hitting `*`.
        {
          path: '/easy-setup-mx/*',
          element: <MxHandoffRedirect from="/easy-setup-mx" to="/easy-setup" />,
          errorElement: <ErrorElement />,
        },
        {
          path: '/setup-mx/*',
          element: <MxHandoffRedirect from="/setup-mx" to="/setup" />,
          errorElement: <ErrorElement />,
        },
        // Legacy → renamed configurator paths (see LegacyPathRedirect). Prepended
        // ahead of the config routes so a stale marketing deep link redirects
        // cleanly instead of falling through to the `*` catch-all.
        ...LEGACY_CONFIGURATOR_REDIRECTS.map((r) => ({
          path: `${r.from}/*`,
          element: <LegacyPathRedirect from={r.from} to={r.to} />,
          errorElement: <ErrorElement />,
        })),
        ...fixedRoutes,
        ...routes,
      ],
    },
  ]);

  storedRouter = router;

  return <RouterProvider router={router} />;
};

export default Routes;
